import React, { useMemo, useState, useCallback } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { turndown } from 'markdown-read/dist/turndown'
import { useTranslations } from 'next-intl'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import Link from 'next/link'

import Header from '../components/layouts/header'
import { Seo } from '@/components/seo'
import HTMLDoc from '../docs/html2md.mdx'
import HTMLDocEn from '../docs/html2md.en.mdx'
import { 
  HTMLEditor, 
  MarkdownViewer 
} from '@/components/markdown-converter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

const defaultZhHtml = `<!-- 请在这里输入 HTML 代码，可使用 VSCode 快捷键进行编辑 -->
<!-- 支持表格、列表、代码等基本所有的格式，如果有所问题，可在以下方式联系我 -->

<h1>开发者武器库 - 程序员的专业工具箱</h1>

<p>
  <strong>欢迎访问 <a href="https://devtool.tech">DevTool</a></strong>
</p>

<p>本工具地址: <a href="http://devtool.tech/html-md">Devtool/HTML-To-Markdown</a></p>

<p><strong>如果需要在 URL 生成 Markdown 后进行某些修改，建议使用本站另一工具：<a href="https://markdown.devtool.tech/app">码途编辑器</a>，并进行如下操作。
</strong></p>

<img src="https://static.shanyue.tech/images/23-09-21/clipboard-5477.c45893.webp" className="w-2/3 border rounded shadow" width="60%" />

<h2>联系方式</h2>

<ul>
  <li>
    微信: shanyue94(山月)
  </li>
  <li>
    Github: <a href="https://github.com/shfshanyue">shfshanyue</a>
  </li>
  <li>
    博客: <a href="https://shanyue.tech">shanyue.tech</a>
  </li>
</ul>

<h2>联系方式: 表格版</h2>

<table>
<thead>
<tr>
<th style="text-align:left">账号</th>
<th style="text-align:center">地址</th>
</tr>
</thead>
<tbody>
<tr>
<td>微信</td>
<td>shanyue94(山月)</td>
</tr>
<tr>
<td>Github</td>
<td>shfshanyue</td>
</tr>
<tr>
<td>博客</td>
<td>shanyue.tech</td>
</tr>
</tbody>
</table>
`

const defaultEnHtml = `<!-- Please enter HTML code here, you can use VSCode shortcuts for editing -->
<!-- Supports tables, lists, code, and almost all basic formats. If you have any issues, please contact me using the methods below -->

<h1>Developer's Arsenal - Professional Toolbox for Programmers</h1>

<p>
  <strong>Welcome to <a href="https://devtool.tech">DevTool</a></strong>
</p>

<p>Tool address: <a href="http://devtool.tech/html-md">Devtool/HTML-To-Markdown</a></p>

<p><strong>If you need to make some modifications after generating Markdown from a URL, we recommend using another tool on this site: <a href="https://markdown.devtool.tech/app">Markdown Editor</a>, and follow the steps below.
</strong></p>

<img src="https://static.shanyue.tech/images/23-09-21/clipboard-5477.c45893.webp" className="w-2/3 border rounded shadow" width="60%" />

<h2>Contact Information</h2>

<ul>
  <li>
    WeChat: shanyue94 (Shanyue)
  </li>
  <li>
    Github: <a href="https://github.com/shfshanyue">shfshanyue</a>
  </li>
  <li>
    Blog: <a href="https://shanyue.tech">shanyue.tech</a>
  </li>
</ul>

<h2>Contact Information: Table Version</h2>

<table>
<thead>
<tr>
<th style="text-align:left">Account</th>
<th style="text-align:center">Address</th>
</tr>
</thead>
<tbody>
<tr>
<td>WeChat</td>
<td>shanyue94 (Shanyue)</td>
</tr>
<tr>
<td>Github</td>
<td>shfshanyue</td>
</tr>
<tr>
<td>Blog</td>
<td>shanyue.tech</td>
</tr>
</tbody>
</table>
`

function HTMLToMarkdown () {
  const t = useTranslations('HTMLToMarkdown')
  const { locale } = useRouter()
  const [code, setCode] = useState(locale === 'en' ? defaultEnHtml : defaultZhHtml)

  // 使用 useMemo 缓存 markdown 转换结果，避免不必要的计算
  const markdown = useMemo(() => turndown(code), [code])

  // 重置编辑器内容
  const handleReset = useCallback(() => {
    setCode(locale === 'en' ? defaultEnHtml : defaultZhHtml)
    toast.success(t('resetText'))
  }, [locale, t])

  // 复制 Markdown 内容
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(markdown)
    toast.success(t('copySuccess'))
  }, [markdown, t])

  return (
    <div className="min-h-screen">
      <Seo
        title={t('pageTitle')}
        description={t('metaDescription')}
        path="/html-md"
        thumbnail="https://devtool.tech/thumbnail/htmlmd.png"
      />
      <Header title={t('headerTitle')} />
      <div className="container mx-auto px-4 py-8">
        <Toaster />

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('convertTitle')}</CardTitle>
            <CardDescription>{t('convertDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col space-y-2 mb-4">
              <Link
                href="/batch-url-md"
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <span className="mr-1">📚</span>
                {t('batchToolLink')}
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* HTML编辑器 */}
              <div className="border border-gray-200 dark:border-gray-800 rounded-md overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-medium text-gray-800 dark:text-gray-200">{t('htmlTitle')}</h3>
                </div>
                <div className="h-[calc(100vh-26rem)]">
                  <HTMLEditor
                    code={code}
                    onCodeChange={setCode}
                    urlPlaceholder={t('urlInputPlaceholder')}
                    invalidUrlMessage={t('invalidUrl')}
                    urlParseSuccess={t('urlParseSuccess')}
                    urlParseError={t('urlParseError')}
                    urlParsing={t('urlParsing')}
                    batchToolMessage={t('batchToolLink')}
                  />
                </div>
              </div>

              {/* Markdown预览 */}
              <div className="border border-gray-200 dark:border-gray-800 rounded-md overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-medium text-gray-800 dark:text-gray-200">{t('previewTitle')}</h3>
                </div>
                <div className="h-[calc(100vh-26rem)]">
                  <MarkdownViewer
                    markdown={markdown}
                    title={t('previewTitle')}
                    containerClassName="h-full"
                    copyButtonClassName="absolute right-2 top-2"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-4">
              <Button
                variant="outline"
                onClick={handleReset}
                className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
              >
                {t('resetText')}
              </Button>

              <Button
                variant="secondary"
                onClick={handleCopy}
              >
                {t('copyText')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Separator className="my-8" />

        <div className="prose prose-blue max-w-none dark:prose-invert">
          {locale === 'en' ? <HTMLDocEn /> : <HTMLDoc />}
        </div>
      </div>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      messages: (await import(`../messages/${locale}.json`)).default
    }
  }
}

export default HTMLToMarkdown
